import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, lazy, Suspense } from "react";

const InsightsCharts = lazy(() => import("@/components/insights/InsightsCharts"));
const InsightsMobile = lazy(() => import("@/components/insights/InsightsMobile"));


import {
  ArrowLeft,
  Crown,
  Disc3,
  Sparkles,
  TrendingUp,
  Wallet,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { PageHero } from "@/components/PageHero";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useIsMobile } from "@/hooks/use-mobile";

import { listMyCollection, type CollectionItem } from "@/lib/collection.functions";
import { getArtistImages } from "@/lib/artist-images.functions";

export const Route = createFileRoute("/insights")({
  head: () => ({
    meta: [
      { title: "Insights da coleção — Viny Club" },
      {
        name: "description",
        content:
          "Panorama visual da sua coleção de vinil: quanto você investiu, gêneros, décadas, artistas e a relíquia da sua estante.",
      },
      { property: "og:title", content: "Insights da coleção — Viny Club" },
      {
        property: "og:description",
        content:
          "Gráficos, capas e a relíquia da sua coleção de vinil em uma única tela.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: InsightsPage,
});

const brl = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const GENRE_COLORS = [
  "var(--color-primary)",
  "#d4af37",
  "#e08a5f",
  "#7aa2a0",
  "#9b8bd0",
  "#c4635a",
];

function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
}

function InsightsPage() {
  const { user, profile, loading: authLoading } = useCurrentUser();
  const isMobile = useIsMobile();
  const collectionFn = useServerFn(listMyCollection);


  const q = useQuery({
    queryKey: ["my-collection", user?.id ?? null],
    queryFn: () => collectionFn(),
    enabled: !!user,
    retry: false,
  });

  const items = q.data ?? [];
  const stats = useMemo(() => computeStats(items), [items]);

  const artistsFn = useServerFn(getArtistImages);
  const artistNames = stats.topArtists.map(([a]) => a);
  const artistImgs = useQuery({
    queryKey: ["artist-images", artistNames.join("|")],
    queryFn: () => artistsFn({ data: { artists: artistNames } }),
    enabled: artistNames.length > 0,
    staleTime: 1000 * 60 * 60 * 24,
  });

  const name = profile?.display_name || profile?.username || "Colecionador";


  return (
    <AppShell>
      <style>{REVEAL_CSS}</style>



      <PageHero
        eyebrow="Coleção"
        title="Insights da coleção"
        subtitle={`${name}, aqui está o retrato da sua estante: quanto vale, o que te define e qual disco é a relíquia da casa.`}
        actions={
          <Link
            to="/collection"
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.06] px-3.5 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-white/[0.12]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Minha coleção
          </Link>
        }
      />


      <div className="mx-auto w-full max-w-[1400px] px-4 pb-20 lg:px-5">
        {!authLoading && !user ? (
          <div className="py-20 text-center">
            <p className="text-sm text-muted-foreground">
              Entre na sua conta para ver os insights da sua coleção.
            </p>
            <Link
              to="/auth"
              className="mt-5 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
            >
              Entrar
            </Link>
          </div>
        ) : q.isLoading ? (
          <div className="grid gap-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl bg-surface/50" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center">
            <Disc3 className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">
              Cadastre discos na sua coleção para desbloquear os insights.
            </p>
          </div>
        ) : isMobile ? (
          <div className="space-y-4 py-6">
            <Suspense
              fallback={<div className="h-64 animate-pulse rounded-3xl bg-surface/40" />}
            >
              <InsightsMobile
                total={stats.total}
                invested={stats.invested}
                avg={stats.avg}
                projected={stats.projected}
                priced={stats.priced}
                withoutPrice={stats.withoutPrice}
                growth={stats.growth}
                genres={stats.genres}
                decades={stats.decades}
                topArtists={stats.topArtists}
                topDecade={stats.topDecade}
                oldestAlbum={stats.oldestAlbum}
              />
            </Suspense>
            <Reliquia stats={stats} />
            <MostExpensive stats={stats} />
          </div>
        ) : (
          <div className="space-y-12 py-10">
            <Reveal><MetricRow stats={stats} /></Reveal>
            <Reveal><ChartsRow stats={stats} /></Reveal>
            <Reveal><Reliquia stats={stats} /></Reveal>
            <Reveal><TopArtists stats={stats} images={artistImgs.data ?? {}} /></Reveal>
            <Reveal><MostExpensive stats={stats} /></Reveal>
          </div>
        )}


      </div>
    </AppShell>
  );
}

/* ---------------- stats ---------------- */

type Stats = ReturnType<typeof computeStats>;

function computeStats(items: CollectionItem[]) {
  const total = items.length;
  const priced = items.filter((a) => (a.paid_price ?? 0) > 0);
  const invested = priced.reduce((s, a) => s + (a.paid_price ?? 0), 0);
  const avg = priced.length ? invested / priced.length : 0;
  const listedValue = items.reduce((s, a) => s + (a.sale_price ?? a.price_brl ?? 0), 0);

  const sortedByPrice = [...priced].sort((a, b) => (b.paid_price ?? 0) - (a.paid_price ?? 0));
  const crown = sortedByPrice[0] ?? null;
  const topFive = sortedByPrice.slice(0, 6);

  const genreMap = new Map<string, number>();
  for (const a of items) for (const g of a.genres ?? []) genreMap.set(g, (genreMap.get(g) ?? 0) + 1);
  const genres = [...genreMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);

  const decadeMap = new Map<number, number>();
  for (const a of items) {
    if (!a.year) continue;
    const d = Math.floor(a.year / 10) * 10;
    decadeMap.set(d, (decadeMap.get(d) ?? 0) + 1);
  }
  const decades = [...decadeMap.entries()].sort((a, b) => a[0] - b[0]);
  const topDecade = [...decadeMap.entries()].sort((a, b) => b[1] - a[1])[0] ?? null;

  const artistMap = new Map<string, { count: number; cover: string | null }>();
  for (const a of items) {
    const prev = artistMap.get(a.artist);
    artistMap.set(a.artist, {
      count: (prev?.count ?? 0) + 1,
      cover: prev?.cover ?? a.cover_url ?? null,
    });
  }
  const topArtists = [...artistMap.entries()]
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 6)
    .map(([n, v]) => [n, v.count, v.cover] as const);

  const years = items.map((a) => a.year).filter((y): y is number => !!y);
  const oldest = years.length ? Math.min(...years) : null;
  const oldestAlbum =
    [...items].filter((a) => !!a.year).sort((a, b) => (a.year ?? 0) - (b.year ?? 0))[0] ?? null;

  const withoutPrice = total - priced.length;
  const projected = avg > 0 ? invested + avg * withoutPrice : invested;

  // cumulative growth by month (created_at)
  const monthMap = new Map<string, { discos: number; valor: number }>();
  for (const a of items) {
    const key = (a.created_at ?? "").slice(0, 7);
    if (!key) continue;
    const prev = monthMap.get(key) ?? { discos: 0, valor: 0 };
    monthMap.set(key, {
      discos: prev.discos + 1,
      valor: prev.valor + (a.paid_price ?? 0),
    });
  }
  let accD = 0;
  let accV = 0;
  const growth = [...monthMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-12)
    .map(([month, v]) => {
      accD += v.discos;
      accV += v.valor;
      const [y, m] = month.split("-");
      return {
        label: `${m}/${(y ?? "").slice(2)}`,
        discos: accD,
        valor: Math.round(accV),
        novos: v.discos,
      };
    });

  // with a single month there is no curve to draw — prepend a zero baseline
  if (growth.length === 1) {
    const only = growth[0]!;
    const [m, y] = only.label.split("/");
    const prevMonth = Number(m) === 1 ? 12 : Number(m) - 1;
    const prevYear = Number(m) === 1 ? Number(y) - 1 : Number(y);
    growth.unshift({
      label: `${String(prevMonth).padStart(2, "0")}/${String(prevYear).padStart(2, "0")}`,
      discos: 0,
      valor: 0,
      novos: 0,
    });
  }


  const covers = items.map((a) => a.cover_url).filter((c): c is string => !!c);

  return {
    total,
    invested,
    avg,
    listedValue,
    priced: priced.length,
    withoutPrice,
    projected,
    crown,
    topFive,
    genres,
    decades,
    topDecade,
    topArtists,
    oldest,
    oldestAlbum,

    growth,
    covers,
  };
}

/* ---------------- metrics ---------------- */

function MetricRow({ stats }: { stats: Stats }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={Wallet}
          label="Investido"
          value={brl(stats.invested)}
          hint={`${stats.priced} de ${stats.total} discos com preço`}
          accent
        />
        <MetricCard
          icon={TrendingUp}
          label="Valor estimado"
          value={brl(stats.projected)}
          hint="Projeção usando o preço médio nos discos sem valor"
        />
        <MetricCard icon={Disc3} label="Preço médio" value={brl(stats.avg)} hint="Por disco pago" />
        <MetricCard
          icon={Sparkles}
          label="Anunciado"
          value={brl(stats.listedValue)}
          hint="Soma dos discos com preço de venda"
        />
      </div>
      {stats.withoutPrice > 0 && (
        <div className="rounded-2xl border border-border bg-surface/40 px-5 py-4 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{stats.withoutPrice} discos</span> ainda
          não têm quanto você pagou. Preencha nos detalhes do disco para o panorama ficar exato.
        </div>
      )}
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-5 ${
        accent ? "border-primary/40 bg-primary/[0.06]" : "border-border bg-surface/40"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-3 font-display text-3xl font-bold leading-none">{value}</div>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{hint}</p>
    </div>
  );
}

function Panel({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-2xl border border-border bg-surface/25 p-5 ${className ?? ""}`}>
      <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {title}
      </h2>
      {subtitle && <p className="mt-1 text-xs text-muted-foreground/80">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/* ---------------- charts (lazy: recharts is heavy) ---------------- */

function ChartsRow({ stats }: { stats: Stats }) {
  return (
    <Suspense
      fallback={
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="h-72 animate-pulse rounded-2xl bg-surface/40 lg:col-span-2" />
          <div className="h-72 animate-pulse rounded-2xl bg-surface/40" />
          <div className="h-60 animate-pulse rounded-2xl bg-surface/40 lg:col-span-3" />
        </div>
      }
    >
      <InsightsCharts
        growth={stats.growth}
        genres={stats.genres}
        decades={stats.decades}
        artists={stats.topArtists.map(([n, c]) => [n, c] as [string, number])}
        hasPrices={stats.priced > 0}
        total={stats.total}
      />
    </Suspense>
  );
}


/* ---------------- relíquia (mesma tela) ---------------- */

function Reliquia({ stats }: { stats: Stats }) {
  const crown = stats.oldestAlbum ?? stats.crown;
  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-[#d4af37]/25 bg-[#0d0b0a] p-6 text-[#f5f1ea] lg:p-9"
      style={{
        backgroundImage:
          "radial-gradient(110% 80% at 20% 0%, rgba(212,175,55,0.22), transparent 62%)",
      }}
    >
      {crown?.cover_url && (
        <img
          aria-hidden
          src={crown.cover_url}
          alt=""
          loading="lazy"
          className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rotate-6 rounded-3xl object-cover opacity-20 blur-[2px]"
        />
      )}
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-[#d4af37]">
          <Crown className="h-3.5 w-3.5" /> Relíquia da coleção
        </span>

        {crown ? (
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end">
            <div className="h-44 w-44 shrink-0 overflow-hidden rounded-2xl bg-black/40 shadow-2xl ring-1 ring-[#d4af37]/30 lg:h-56 lg:w-56">
              {crown.cover_url && (
                <img
                  src={crown.cover_url}
                  alt={`Capa de ${crown.title}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] opacity-60">
                O disco mais antigo da sua coleção
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold leading-tight lg:text-5xl">
                {crown.title}
              </h2>
              <p className="mt-1 text-base opacity-70">
                {crown.artist} · {crown.year ?? "—"}
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <ReliquiaStat
                  label="Ano de lançamento"
                  value={crown.year ? String(crown.year) : "—"}
                />
                <ReliquiaStat
                  label="Pago nela"
                  value={crown.paid_price ? brl(crown.paid_price) : "Não informado"}
                />
                <ReliquiaStat label="Discos" value={String(stats.total)} />
                <ReliquiaStat
                  label="Década favorita"
                  value={stats.topDecade ? `${stats.topDecade[0]}s` : "—"}
                />
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm opacity-70">
            Cadastre o ano dos seus discos para descobrir a relíquia da sua coleção.
          </p>
        )}


        {stats.genres.length > 0 && (
          <div className="mt-7 flex flex-wrap gap-2 border-t border-white/10 pt-5">
            {stats.genres.slice(0, 5).map(([g]) => (
              <span
                key={g}
                className="rounded-full border border-[#d4af37]/30 px-3 py-1 text-[11px] font-semibold text-[#e8dcc0]"
              >
                {g}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ReliquiaStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white/[0.07] px-4 py-3">
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] opacity-60">{label}</div>
      <div className="mt-1 font-display text-lg font-bold">{value}</div>
    </div>
  );
}

/* ---------------- artistas ---------------- */

function TopArtists({
  stats,
  images,
}: {
  stats: Stats;
  images: Record<string, string | null>;
}) {
  if (stats.topArtists.length === 0) return null;
  const max = Math.max(...stats.topArtists.map(([, c]) => c));
  return (
    <Panel title="Artistas mais presentes" subtitle="Fotos via Spotify">
      <div className="grid gap-2.5 sm:grid-cols-2 sm:gap-x-6">
        {stats.topArtists.map(([artist, count, cover], i) => {
          const img = images[artist] ?? cover ?? null;
          return (
            <div key={artist} className="flex items-center gap-3">
              <span className="w-4 shrink-0 text-right font-display text-[13px] tabular-nums text-muted-foreground">
                {i + 1}
              </span>
              <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full bg-surface ring-1 ring-border">
                {img ? (
                  <img
                    src={img}
                    alt={`Foto de ${artist}`}
                    loading="lazy"
                    decoding="async"
                    width={44}
                    height={44}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="grid h-full place-items-center">
                    <Disc3 className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold">{artist}</div>
                <div className="mt-1 h-1 w-full max-w-[120px] overflow-hidden rounded-full bg-surface">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${(count / max) * 100}%` }}
                  />
                </div>
              </div>
              <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {count} {count === 1 ? "disco" : "discos"}
              </span>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}


/* ---------------- mais caros ---------------- */

function MostExpensive({ stats }: { stats: Stats }) {
  if (stats.topFive.length === 0) return null;
  const max = stats.topFive[0]?.paid_price ?? 1;
  return (
    <Panel title="Discos mais caros" subtitle="Pelo valor que você pagou">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stats.topFive.map((a, i) => (
          <div
            key={a.id}
            className="flex min-w-0 items-center gap-3 rounded-2xl border border-border bg-background/40 p-3"
          >
            <span className="w-3 shrink-0 font-display text-sm font-bold text-muted-foreground">
              {i + 1}
            </span>
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-surface sm:h-16 sm:w-16">
              {a.cover_url && (
                <img
                  src={a.cover_url}
                  alt={`Capa de ${a.title}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{a.title}</div>
              <div className="truncate text-xs text-muted-foreground">{a.artist}</div>
              <div className="mt-1.5 font-display text-sm font-bold">
                {brl(a.paid_price ?? 0)}
              </div>
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-surface">
                <div
                  className="h-full rounded-full bg-[#d4af37]"
                  style={{ width: `${((a.paid_price ?? 0) / (max || 1)) * 100}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

    </Panel>
  );
}

/* ---------------- reveal on scroll ---------------- */

function Reveal({ children }: { children: React.ReactNode }) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [el]);

  return (
    <div ref={setEl} className={shown ? "ins-reveal is-in" : "ins-reveal"}>
      {children}
    </div>
  );
}

const REVEAL_CSS = `
@keyframes insIn { from { opacity:0; transform: translateY(28px); filter: blur(8px);} to { opacity:1; transform:none; filter:blur(0);} }
.ins-in { animation: insIn .9s cubic-bezier(.16,1,.3,1) both; }
.ins-d2 { animation-delay:.12s } .ins-d3 { animation-delay:.24s }
.ins-reveal { opacity:0; transform: translateY(34px); filter: blur(8px); transition: opacity .8s cubic-bezier(.16,1,.3,1), transform .8s cubic-bezier(.16,1,.3,1), filter .8s ease; }
.ins-reveal.is-in { opacity:1; transform:none; filter:blur(0); }
@media (prefers-reduced-motion: reduce) {
  .ins-in { animation: none !important; }
  .ins-reveal { opacity:1 !important; transform:none !important; filter:none !important; transition:none !important; }
}
`;
